import { datadogRum } from '@datadog/browser-rum';
import type { ReactElement, VoidFunctionComponent } from 'react';
import type { InjectedRouter, RouterState } from 'react-router';
import type { Params } from 'react-router/lib/Router';
import ApplicationMobileWarning from './components/ApplicationMobileWarning';
import DialogManager from './components/DialogManager';
import StatusBanner from './components/generic/StatusBanner';
import JobNotificationProvider from './components/JobNotificationHub/Provider';
import Toaster from './components/Toaster/Toaster';
import DialogContextProvider from './contexts/DialogContext';
import ToastContextProvider from './contexts/ToastContext';
import { window } from './globals';
import * as utils from './utils';
import RouterContextProvider from './contexts/RouterContext';
import { Provider as PortalUrlProvider } from './components/shared/publicPortal/context/portalUrlContext';
import { instrumentFetch } from './utils/tracing';
import { useOnMount, useOnUnmount } from './hooks';
import PlanSelectorContextProvider, { usePlanSelectorContext } from './contexts/PlanSelectorContext';
import type { MessageOptions } from './components/settings/billing/features/consts';
import TrialEndSurveyModal from './components/trialEndSurvey/TrialEndSurveyModal';
import CommandPaletteProvider from './components/CommandPalette';

type ApplicationProps = {
  children: ReactElement;
  location: RouterState['location'];
  router: InjectedRouter & { params: Params};
};

const enableDatadogRUM = () => {
  if (!utils.isTrue(window.datadogRUM?.enabled)) {
    return;
  }
  instrumentFetch(); // this must be called before datadogRum.init
  datadogRum.init({
    version: window.fossa_version,
    ...window.datadogRUM,
  });

  if (window.user) {
    datadogRum.setUser({
      id: `${window.user.id}`,
      name: window.user.username,
      email: window.user.email,
      organizationId: window.user.organizationId,
      organizationTitle: window.user.organization?.title,
    });
  }
};

const enableDevrev = () => {
  if (!window.devrev.appId || !window.plugSDK) {
    return;
  }

  window.plugSDK.init({
    app_id: window.devrev.appId,
    session_token: window.devrev.sessionToken,
  });
};

// Send user id to heap, so we can use it to identify the user in the heap dashboard.
const identifyHeapUser = () => {
  if (window.heap && window.user?.id) {
    window.heap.identify(window.user.id.toString());
    window.heap.addUserProperties({
      email: window.user.email,
      createdAt: window.user.createdAt,
      organizationId: window.user.organizationId,
      organization: window.user.organization?.title,
    });
  }
};

type PlansModalContextWrapperProps = {
  children: ReactElement;
};

const PlansModalContextWrapper: VoidFunctionComponent<PlansModalContextWrapperProps> = ({
  children,
}: PlansModalContextWrapperProps) => {
  const { showPlanSelectorModal } = usePlanSelectorContext();

  useOnMount(() => {
    window.showPlanSelectorModal = (messageOptions?: MessageOptions) => showPlanSelectorModal(messageOptions);
  });

  useOnUnmount(() => {
    // @ts-expect-error - TODO - remove this global state - CORE-4179
    delete window.showPlanSelectorModal;
  });

  return children;
};

const Application: VoidFunctionComponent<ApplicationProps> = ({
  children,
  location,
  router,
}: ApplicationProps) => {
  useOnMount(() => {
    enableDatadogRUM();
    identifyHeapUser();
    enableDevrev();
    utils.showDelightedSurveyDefault();
  });

  return (
    <PortalUrlProvider>
      <DialogContextProvider>
        <ToastContextProvider>
          <RouterContextProvider router={router} location={location}>
            <JobNotificationProvider>
              <CommandPaletteProvider>
                {utils.isAuthed() && <ApplicationMobileWarning />}
                <StatusBanner />
                <PlanSelectorContextProvider>
                  <PlansModalContextWrapper>
                    {children}
                  </PlansModalContextWrapper>
                  <TrialEndSurveyModal />
                </PlanSelectorContextProvider>
                <DialogManager />
                <Toaster location={location} />
              </CommandPaletteProvider>
            </JobNotificationProvider>
          </RouterContextProvider>
        </ToastContextProvider>
      </DialogContextProvider>
    </PortalUrlProvider>
  );
};

export default Application;
